import { Target, Handshake, ScrollText, Users2 } from "lucide-react";
import MissionIllustration from "../illustrations/MissionIllustration";
import Reveal, { Stagger, StaggerItem } from "../motion/Reveal";

const PILLARS = [
  { icon: Target, number: "01", label: "Strategic FCNR investing", copy: "NRE to NRO flows optimized for Indian rupee gains and forex windows." },
  { icon: Handshake, number: "02", label: "SEBI-registered advisors", copy: "Direct access to India-resident experts handling overseas assets." },
  { icon: ScrollText, number: "03", label: "FEMA & RBI compliant", copy: "USD 1M repatriation, Form 67, TCS and DTAA relief handled." },
  { icon: Users2, number: "04", label: "Generational wealth", copy: "Legal structures for inheritance tax planning and HUF setup." },
];

export default function Mission() {
  return (
    <section
      id="mission"
      data-testid="mission-section"
      className="py-20 lg:py-28 relative overflow-hidden"
      style={{ background: "rgba(255,255,255,0.18)" }}
    >
      {/* Indian cultural backdrop - layered gradients with Ashoka blue and saffron */}
      <div
        aria-hidden
        className="absolute bottom-[-15%] left-[-10%] w-[480px] h-[480px] rounded-full opacity-[0.12] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-gold)) 0%, transparent 65%)" }}
      />
      <div
        aria-hidden
        className="absolute top-[-5%] right-[-8%] w-[400px] h-[400px] rounded-full opacity-[0.10] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-teal)) 0%, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full opacity-[0.06] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-sage)) 0%, transparent 70%)" }}
      />
      {/* Subtle accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[hsl(var(--zt-sage))]/60 to-transparent opacity-50" />

      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-14 lg:gap-20 items-center relative z-10">

        {/* Left: Mission flow illustration */}
        <Reveal className="lg:col-span-5 relative" y={24}>
          <MissionIllustration />
        </Reveal>

        {/* Right: copy & pillars */}
        <div className="lg:col-span-7 space-y-7">
          <Reveal delay={0.05}>
            <div className="flex items-center gap-3">
              <span className="gold-rule" style={{background: "linear-gradient(90deg, hsl(var(--zt-gold)), hsl(var(--zt-teal)))"}} />
              <span className="eyebrow" style={{color: "hsl(var(--zt-gold))"}}>Who We Serve</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="font-display text-[2rem] md:text-[2.6rem] lg:text-[3.1rem] leading-[1.1] text-[hsl(var(--zt-teal-deep))]">
              Your bridge between{" "}
              <span className="text-[hsl(var(--zt-gold))]">foreign earnings</span> and{" "}
              <span className="text-[hsl(var(--zt-sage))]">Indian wealth</span>.
            </h2>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="text-[1.0625rem] text-[hsl(var(--zt-charcoal))]/85 leading-[1.7] max-w-2xl font-body">
              Built for NRIs, FCNR account holders, and overseas Indians. One dashboard for FEMA compliance, 
              rupee repatriation, tax filing, and generational wealth planning — all guided by SEBI advisors who know India's rules.
            </p>
          </Reveal>

          <Stagger className="grid sm:grid-cols-2 gap-4 pt-4" staggerChildren={0.09}>
            {PILLARS.map((p) => (
              <StaggerItem key={p.number}>
                <Pillar {...p} />
              </StaggerItem>
            ))}
          </Stagger>
        </div>

      </div>
    </section>
  );
}

function Pillar({ icon: Icon, number, label, copy }) {
  return (
    <div className="group rounded-xl bg-white border-2 border-[hsl(var(--zt-beige-deep))] p-5 h-full transition-all duration-300 hover:border-[hsl(var(--zt-gold))]/60 hover:-translate-y-0.5 elev-1 hover:elev-2 hover:shadow-lg hover:shadow-[hsl(var(--zt-gold))]/10">
      <div className="flex items-center justify-between mb-3.5">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[hsl(var(--zt-gold))]/15 to-[hsl(var(--zt-gold))]/5 grid place-items-center transition-all duration-300 group-hover:from-[hsl(var(--zt-gold))]/25 group-hover:to-[hsl(var(--zt-gold))]/12 group-hover:shadow-md group-hover:shadow-[hsl(var(--zt-gold))]/20">
          <Icon size={18} className="text-[hsl(var(--zt-gold))] group-hover:scale-110 transition-transform" />
        </div>
        <span className="font-numeric text-[11px] font-bold text-[hsl(var(--zt-gold))] bg-[hsl(var(--zt-gold))]/8 px-2.5 py-1 rounded-md">{number}</span>
      </div>
      <p className="font-heading text-[14.5px] font-semibold text-[hsl(var(--zt-teal-deep))] leading-snug">{label}</p>
      <p className="font-body text-[13px] text-[hsl(var(--zt-charcoal))]/75 leading-relaxed mt-1.5">{copy}</p>
    </div>
  );
}
